// Mock API for development
// This would be replaced with real API calls to the CMS in the production version

// Sample data for Problem category
const problemCategory = {
  title: 'Problem',
  slug: 'problem',
  intro: `<p>
    The problem comes to you - it could be something you've noticed, something you've experienced, something you've heard about and can often manifest itself as an idea of a solution to this imagined problem.
    </p>
    <p>
    However, taking the time to fully understand the problem itself first will make the likelihood of finding the right solution much higher.
    </p>
    <p>
    This section covers the discovery of that problem, and the various tools that can be used to frame, prioritise and define it further. 
    </p>
    <h4>Problem Tools</h4>`,
  subcategories: [
    {
      title: 'Discovery',
      slug: 'discovery',
      tools: [
        {
          title: 'Problem Log',
          slug: 'problem-log',
          description: 'Keep track of problems to be further investigated.',
          resources: [
            { type: 'pdf', href: '/problem/discovery/problem-log.pdf' },
            { type: 'miro', href: 'https://miro.com/app/board/o9J_kpGUsJk=/' }
          ]
        },
        {
          title: 'Problem Workshop',
          slug: 'workshop',
          description: 'Bring a team together to discuss, analyse and prioritise problems to be solved.',
          resources: []
        },
        {
          title: 'Problem Interview',
          slug: 'problem-interview',
          description: 'Discover the problem from the source through an interview.',
          resources: [
            { type: 'pdf', href: '/problem/discovery/problem-interview-sheet.pdf' },
            { type: 'miro', href: 'https://miro.com/app/board/o9J_kpWNnvM=/' }
          ]
        },
        {
          title: '5 Whys',
          slug: '5-whys',
          description: 'Go deeper with understanding the problem.',
          resources: [
            { type: 'pdf', href: '/problem/discovery/5-whys.pdf' },
            { type: 'miro', href: 'https://miro.com/app/board/o9J_kpXATWs=/' }
          ]
        },
        {
          title: 'Sailboat',
          slug: 'sailboat',
          description: 'Discover future problems that may occur.',
          resources: [
            { type: 'pdf', href: '/problem/discovery/sailboat.pdf' },
            { type: 'miro', href: 'https://miro.com/app/board/o9J_kpKGzwI=/' }
          ]
        }
      ]
    },
    {
      title: 'Prioritisation',
      slug: 'prioritisation',
      tools: [
        {
          title: 'Priority Matrix',
          slug: 'priority-matrix',
          description: 'Visually plot problems in order to prioritise which should be worked on.',
          resources: [
            { type: 'pdf', href: '/problem/prioritisation/priority-matrix.pdf' },
            { type: 'miro', href: 'https://miro.com/app/board/o9J_kpPWRDY=/' }
          ]
        },
        {
          title: 'SWOT Analysis',
          slug: 'swot-analysis',
          description: 'Look inwards to help focus on the problems that are aligned with the business goals.',
          resources: [
            { type: 'pdf', href: '/problem/prioritisation/swot-analysis.pdf' },
            { type: 'miro', href: 'https://miro.com/app/board/o9J_kpd-ZfQ=/' }
          ]
        }
      ]
    }
  ]
};

// Sample data for Research category
const researchCategory = {
  title: 'Research',
  slug: 'research',
  intro: `<p>Research is the foundation of any problem solving process. It helps us understand the problem space, the users, and the context in which the solution will be used.</p>
  <p>This section provides tools to help conduct user research, market research, and other types of research needed to inform your solution.</p>
  <h4>Research Tools</h4>`,
  subcategories: [
    {
      title: 'Empathise',
      slug: 'empathise',
      tools: [
        {
          title: 'User Interview',
          slug: 'user-interview',
          description: 'Conduct interviews with users to understand their needs, behaviors, and pain points.',
          resources: []
        }
      ]
    }
  ]
};

// Sample data for a specific tool (Problem Log)
const problemLogTool = {
  title: 'Problem Log',
  slug: 'problem-log',
  grandparent: 'Research',
  parent: 'Problem',
  child: 'Discovery',
  toolInfo: {
    updated: '10/08/20',
    category: 'Discovery',
    creator: {
      href: 'http://www.creativeeducationfoundation.org/facilitation_tool/5-ws-h/',
      text: 'Sakichi Toyoda'
    }
  },
  tabs: [
    {
      id: 'overview',
      label: 'Overview',
      type: 'overview',
      html: `<p>
      A problem log is a simple way of capturing previously prioritised problems, and tracking problems that should be looked at in more detail in the future.
      </p>
      
      <p>
      Quite often by logging these problems they start to overlap and multiple problems can even be solved by the same solution. 
      </p>
      
      <p>
      As the log is at the beginning of the process it is pre-research, and therefore will contain a lot of assumptions. 
      </p>
      
      <p>
      <span class="press">Note: </span>When these problems have either been tackled or dismissed, simply 'archive' them for future reference. 
      </p>    

      <p>
      The template uses a problem focussed 5 Ws model as a means of logging and clarifying problems. By using the 5 Ws, we can quickly assess and understand the problem. 
      </p>
      
      <p>               
      A simple Now / Next / Later system then helps identify what is being worked on, what will be next and future problems to be looked at. 
      </p>`
    },
    {
      id: 'template',
      label: 'Template',
      type: 'template',
      url: 'https://miro.com/app/embed/o9J_kpGUsJk=/?&pres=1'
    }
  ],
  resources: [
    {
      type: 'pdf',
      href: '/problem/discovery/problem-log.pdf'
    },
    {
      type: 'miro',
      href: 'https://miro.com/app/board/o9J_kpGUsJk=/'
    }
  ]
};

// Mock database for development
const mockDatabase = {
  categories: {
    problem: problemCategory,
    research: researchCategory
  },
  tools: {
    'problem-log': problemLogTool
  }
};

// API for accessing tool and category data
// This would be replaced with real API calls to the CMS in the production version

// Store fetched data cache
let dataCache = null;

// Function to get data once
const fetchData = async () => {
  if (dataCache) {
    console.log('Using cached data:', Object.keys(dataCache.categories).length, 'categories');
    return dataCache;
  }

  try {
    console.log('Fetching data from /data/all-data.json');
    const response = await fetch('/data/all-data.json');
    if (!response.ok) {
      console.error('Failed to fetch data, status:', response.status);
      throw new Error('Failed to fetch data');
    }
    
    dataCache = await response.json();
    console.log('Data fetched successfully:', Object.keys(dataCache.categories).length, 'categories');
    return dataCache;
  } catch (error) {
    console.error('Error fetching data:', error);
    return { categories: {}, tools: {} };
  }
};

// API functions
export const getCategoryData = async (category, subcategory) => {
  try {
    const data = await fetchData();
    
    // Get the category data
    const categoryData = data.categories[category];
    
    if (!categoryData) {
      console.warn(`Category not found: ${category}`);
      return mockDatabase.categories[category] || null;
    }
    
    // If subcategory is specified, filter to just that subcategory
    if (subcategory) {
      const filteredSubcategories = categoryData.subcategories.filter(
        sub => sub.slug === subcategory
      );
      
      if (filteredSubcategories.length === 0) {
        console.warn(`Subcategory not found: ${subcategory} in category ${category}`);
        return mockDatabase.categories[category] || null;
      }
      
      return {
        ...categoryData,
        subcategories: filteredSubcategories
      };
    }
    
    return categoryData;
  } catch (error) {
    console.error('Error fetching category data:', error);
    return mockDatabase.categories[category] || null;
  }
};

export const getToolData = async (category, subcategory, toolId) => {
  try {
    const data = await fetchData();
    
    // Check if we have the data for this category and subcategory
    if (!data.tools[category] || 
        !data.tools[category][subcategory]) {
      console.warn(`Category/subcategory not found: ${category}/${subcategory}`);
      
      // Fall back to mock data for development
      return mockDatabase.tools[toolId] || null;
    }
    
    // Get the tool data
    const toolData = data.tools[category][subcategory][toolId];
    
    if (!toolData) {
      console.warn(`Tool not found: ${toolId} in ${category}/${subcategory}`);
      
      // Fall back to mock data for development
      return mockDatabase.tools[toolId] || null;
    }
    
    // Add the category and subcategory titles
    const categoryData = data.categories[category];
    const subcategoryData = categoryData?.subcategories.find(sub => sub.slug === subcategory);
    
    return {
      ...toolData,
      categoryTitle: categoryData?.title || category,
      subcategoryTitle: subcategoryData?.title || subcategory
    };
  } catch (error) {
    console.error('Error fetching tool data:', error);
    
    // Fall back to mock data for development
    return mockDatabase.tools[toolId] || null;
  }
};

export const getAllCategories = async () => {
  try {
    const data = await fetchData();
    return Object.values(data.categories);
  } catch (error) {
    console.error('Error fetching all categories:', error);
    return Object.values(mockDatabase.categories);
  }
}; 